/**
 * Thin REST client for etm/v1. WordPress cookie auth carries the session;
 * every mutating request needs X-WP-Nonce (CLAUDE.md hard guardrail: never
 * work around nonce/permission checks — this just supplies the header the
 * server already requires). The nonce is per-user, so it's refreshed from
 * the response body whenever auth state changes (register/login return a
 * fresh rest_nonce for the newly-authenticated user), and re-fetched from
 * WP core's rest-nonce endpoint whenever the server rejects it as stale —
 * see refreshNonce() below.
 */

import { __ } from './i18n.js';

const config = window.etmConfig || {};
const API_ROOT = (config.apiRoot || '/wp-json/etm/v1').replace(/\/$/, '');
const AJAX_URL = config.ajaxUrl || '/wp-admin/admin-ajax.php';

let nonce = config.nonce || '';

export function setNonce(next) {
  nonce = next || '';
}

class ApiError extends Error {
  constructor(message, code, status) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/**
 * WP nonces live at most 24h and are bound to the caller's auth context. The
 * shell embeds one at page load, so a PWA kept open past that lifetime — or
 * a shell served stale by any cache — starts sending a dead nonce, and core
 * rejects EVERY REST call carrying it (public routes included) with
 * rest_cookie_invalid_nonce, Dutch: "Cookie controle mislukt". Recovery uses
 * WP core's own refresh endpoint (admin-ajax.php?action=rest-nonce, WP 5.3+,
 * the same one @wordpress/api-fetch uses — core's endpoint, not a custom
 * admin-ajax handler, which stays banned per CLAUDE.md). It responds with a
 * bare nonce string for the current cookie context, logged-in or anonymous.
 * One in-flight refresh is shared by all concurrently failing requests.
 */
let nonceRefresh = null;

function refreshNonce() {
  if (!nonceRefresh) {
    nonceRefresh = fetch(`${AJAX_URL}?action=rest-nonce`, { credentials: 'same-origin' })
      .then((res) => {
        if (!res.ok) throw new Error(`rest-nonce failed: ${res.status}`);
        return res.text();
      })
      .then((text) => {
        const fresh = text.trim();
        // A real nonce is a short hex token; anything else (an HTML error
        // page, a login redirect) must not become our auth header.
        if (!/^[a-f0-9]{6,64}$/i.test(fresh)) {
          throw new Error('rest-nonce: unexpected response');
        }
        nonce = fresh;
      })
      .finally(() => {
        nonceRefresh = null;
      });
  }
  return nonceRefresh;
}

async function request(path, { method = 'GET', body } = {}, isRetry = false) {
  const headers = { 'Content-Type': 'application/json' };
  if (nonce) {
    // Only attach a nonce we actually have — an empty X-WP-Nonce header is
    // itself an invalid nonce to core, turning every call into a 403.
    headers['X-WP-Nonce'] = nonce;
  }

  const res = await fetch(`${API_ROOT}${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    // No/invalid JSON body (e.g. 204, or a proxy error page) — treated as {}.
  }

  if (!res.ok) {
    if (data.code === 'rest_cookie_invalid_nonce') {
      // Stale nonce. Refresh + retry is safe even for POSTs: core runs the
      // nonce check before dispatch (rest_cookie_check_errors on
      // rest_authentication_errors), so a 403 here means no handler ran and
      // nothing mutated. Retry exactly once — a second failure means the
      // problem isn't staleness.
      if (!isRetry) {
        let refreshed = false;
        try {
          await refreshNonce();
          refreshed = true;
        } catch {
          // Refresh itself failed — fall through to the session-expired error.
        }
        if (refreshed) {
          return request(path, { method, body }, true);
        }
      }
      throw new ApiError(
        __('Je sessie is verlopen. Herlaad de app en probeer het opnieuw.', 'etm'),
        data.code,
        res.status
      );
    }
    throw new ApiError(data.message || __('Er ging iets mis. Probeer het opnieuw.', 'etm'), data.code, res.status);
  }
  return data;
}

function qs(params) {
  const clean = Object.fromEntries(
    Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : '';
}

export const api = {
  auth: {
    me: () => request('/auth/me'),
    register: (payload) => request('/auth/register', { method: 'POST', body: payload }),
    login: (payload) => request('/auth/login', { method: 'POST', body: payload }),
    logout: () => request('/auth/logout', { method: 'POST' }),
    forgotPassword: (payload) => request('/auth/forgot-password', { method: 'POST', body: payload }),
    resetPassword: (payload) => request('/auth/reset-password', { method: 'POST', body: payload }),
  },
  categories: {
    list: () => request('/categories'),
    create: (payload) => request('/categories', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/categories/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/categories/${id}`, { method: 'DELETE' }),
  },
  tasks: {
    list: (params) => request(`/tasks${qs(params)}`),
    create: (payload) => request('/tasks', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/tasks/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/tasks/${id}`, { method: 'DELETE' }),
    next: (params) => request(`/tasks/next${qs(params)}`),
    current: () => request('/tasks/current'),
    start: (id) => request(`/tasks/${id}/start`, { method: 'POST' }),
    complete: (id, payload) => request(`/tasks/${id}/complete`, { method: 'POST', body: payload }),
    skip: (id, payload) => request(`/tasks/${id}/skip`, { method: 'POST', body: payload }),
    snooze: (id, payload) => request(`/tasks/${id}/snooze`, { method: 'POST', body: payload }),
    assign: (id, assignedTo) => request(`/tasks/${id}/assign`, { method: 'POST', body: { assigned_to: assignedTo } }),
    calendarBlock: (id) => request(`/tasks/${id}/calendar-block`),
  },
  household: {
    get: () => request('/household'),
    create: (payload) => request('/household', { method: 'POST', body: payload }),
    join: (payload) => request('/household/join', { method: 'POST', body: payload }),
    activity: () => request('/household/activity'),
  },
  // HH-1: household roster ("Gezinsleden"). Household-scoped server-side —
  // there is no id/household param a caller could substitute.
  people: {
    list: () => request('/people'),
    create: (payload) => request('/people', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/people/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/people/${id}`, { method: 'DELETE' }),
  },
  // HH-2: Abonnementen & contracten. Scoped server-side, same as people/*.
  subscriptions: {
    list: () => request('/subscriptions'),
    create: (payload) => request('/subscriptions', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/subscriptions/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/subscriptions/${id}`, { method: 'DELETE' }),
  },
  // HH-3: per-member opt-out for the daily reminder digest. Self-targeted
  // server-side — there is no user id param.
  digest: {
    setPreference: (enabled) => request('/digest/preference', { method: 'POST', body: { enabled } }),
  },
  // HH-4: Boodschappen. Scoped server-side, same as people/subscriptions.
  // These are still plain requests — HH-5's optimistic queue and retry
  // live one layer up (lib/shoplist-sync.js), which calls addItem (with a
  // client_key in the payload for the server-side dedupe) and updateItem
  // through the queue instead of screens calling them directly.
  lists: {
    list: () => request('/lists'),
    get: (id) => request(`/lists/${id}`),
    create: (payload) => request('/lists', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/lists/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/lists/${id}`, { method: 'DELETE' }),
    pour: (id, templateId) => request(`/lists/${id}/pour`, { method: 'POST', body: { template_id: templateId } }),
    itemHistory: () => request('/lists/item-history'),
    addItem: (id, payload) => request(`/lists/${id}/items`, { method: 'POST', body: payload }),
    updateItem: (id, itemId, payload) => request(`/lists/${id}/items/${itemId}`, { method: 'PATCH', body: payload }),
    removeItem: (id, itemId) => request(`/lists/${id}/items/${itemId}`, { method: 'DELETE' }),
    clearChecked: (id) => request(`/lists/${id}/clear-checked`, { method: 'POST' }),
  },
  // HH-8: Projecten. Scoped server-side like people/subscriptions/lists.
  // Project tasks themselves stay on the existing tasks.* client above
  // (list({project_id}) / create({project_id, ...})) — no parallel task
  // surface here, matching the brief's "existing GET /tasks + a
  // project_id filter param" instead of a nested /projects/{id}/tasks.
  projects: {
    list: () => request('/projects'),
    get: (id) => request(`/projects/${id}`),
    create: (payload) => request('/projects', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/projects/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/projects/${id}`, { method: 'DELETE' }),
  },
  // HH-6: Weekritme. Scoped server-side like people/subscriptions/lists.
  // `week` takes only an integer offset — which Monday a week starts on,
  // and what "today" is, are the server's call (see ETM_Schedule::week()),
  // so no date arithmetic crosses this boundary.
  schedule: {
    list: () => request('/schedule'),
    get: (id) => request(`/schedule/${id}`),
    week: (offset) => request(`/schedule/week${qs({ offset })}`),
    create: (payload) => request('/schedule', { method: 'POST', body: payload }),
    update: (id, payload) => request(`/schedule/${id}`, { method: 'PATCH', body: payload }),
    remove: (id) => request(`/schedule/${id}`, { method: 'DELETE' }),
    setSkipped: (id, date, skipped) =>
      request(`/schedule/${id}/skip`, { method: 'POST', body: { date, skipped } }),
  },
  // HH-7: the Vandaag dashboard's one new read. Its other two cards use
  // /schedule/week and /lists, which already exist — deliberately three
  // separate fetches so one failing source hides one card instead of
  // blanking the dashboard.
  dashboard: {
    warnings: () => request('/dashboard/warnings'),
  },
  stats: {
    get: () => request('/stats'),
  },
  calendar: {
    status: () => request('/calendar/status'),
    connect: (provider) => request(`/calendar/connect/${provider}`),
    disconnect: (provider) => request(`/calendar/disconnect/${provider}`, { method: 'POST' }),
    freeBusy: () => request('/calendar/free-busy'),
  },
};

export { ApiError };
